import { useMemo } from 'react';

/**
 * PendantPreview — LCD screen preview component for the PalPendant device.
 *
 * Renders a mini representation of the pendant's LCD screen styled to mimic the
 * actual hardware (dark background, monospace font, rounded corners). Computes
 * display lines from the mode configuration using the same logic as the V1
 * `handleDisplay` function.
 *
 * Props:
 *   modeConfig  — single mode config object { text_size, line_one_data_type_id, ... }
 *   dataTypes   — data type lookup array (for future extensibility)
 *   label       — mode label string (e.g. "Idle", "Panic", "Accepted")
 *   isActive    — boolean, highlight border when this preview is selected
 *   onClick     — click callback to switch active mode
 */

// ---------------------------------------------------------------------------
// Display logic — matches V1 handleDisplay exactly
// ---------------------------------------------------------------------------

/** Sample values per data type for preview rendering. */
const DATA_TYPE_SAMPLES = {
    1: null,            // Custom Text — show custom text only
    2: 'Jane Doe',      // Resident Name
    3: 'Room 101',      // Area/Room
    4: 'Staff Mary',    // Staff Name
};

/**
 * Compute the display text for a single line.
 *
 * @param {number} dataType    — data_type_id (1–4)
 * @param {string} customText  — user-entered custom text
 * @param {boolean} useSuffix  — true = text after data, false = text before
 * @param {number} txtSize     — 1 = 3-line (20 char), 2 = 2-line (14 char)
 * @returns {string} — truncated display text
 */
function computeLineDisplay(dataType, customText, useSuffix, txtSize) {
    if (!dataType) return '';

    const dt = parseInt(dataType);
    const maxLen = txtSize === 1 ? 20 : 14;
    let txtSample = '';

    if (dt === 1) {
        // Custom Text only
        txtSample = `${customText || ''}`;
    } else {
        const sample = DATA_TYPE_SAMPLES[dt] || '';
        if (customText) {
            if (!useSuffix) {
                txtSample = `${customText} ${sample}`;
            } else {
                txtSample = `${sample} ${customText}`;
            }
        } else {
            txtSample = sample;
        }
    }

    return txtSample.slice(0, maxLen);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PendantPreview = ({ modeConfig, dataTypes, label, isActive, onClick }) => {
    const textSize = modeConfig?.text_size || 1;
    const isLargeText = textSize === 2;

    const line1 = useMemo(
        () => computeLineDisplay(
            modeConfig?.line_one_data_type_id,
            modeConfig?.line_one_text,
            modeConfig?.line_one_text_suffix,
            textSize
        ),
        [modeConfig?.line_one_data_type_id, modeConfig?.line_one_text, modeConfig?.line_one_text_suffix, textSize]
    );

    const line2 = useMemo(
        () => computeLineDisplay(
            modeConfig?.line_two_data_type_id,
            modeConfig?.line_two_text,
            modeConfig?.line_two_text_suffix,
            textSize
        ),
        [modeConfig?.line_two_data_type_id, modeConfig?.line_two_text, modeConfig?.line_two_text_suffix, textSize]
    );

    const line3 = useMemo(
        () => computeLineDisplay(
            modeConfig?.line_three_data_type_id,
            modeConfig?.line_three_text,
            modeConfig?.line_three_text_suffix,
            textSize
        ),
        [modeConfig?.line_three_data_type_id, modeConfig?.line_three_text, modeConfig?.line_three_text_suffix, textSize]
    );

    const labelClass = label ? `pp-pendant__label pp-pendant__label--${label.toLowerCase()}` : 'pp-pendant__label';

    return (
        <div
            className={`pp-pendant${isActive ? ' pp-pendant--active' : ''}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            aria-label={`${label} mode preview`}
            aria-pressed={isActive}
        >
            <div className={`pp-pendant__screen${isLargeText ? ' pp-pendant__screen--large' : ''}`}>
                {/* Fixed header bar */}
                <div className="pp-pendant__header">
                    <span>11:35</span>
                    <span>99% T</span>
                </div>

                {/* Display lines */}
                <div className={`pp-pendant__line${isLargeText ? ' pp-pendant__line--large' : ''}`}>
                    {line1 || '\u00a0'}
                </div>
                <div className={`pp-pendant__line${isLargeText ? ' pp-pendant__line--large' : ''}`}>
                    {line2 || '\u00a0'}
                </div>
                {!isLargeText ? (
                    <div className="pp-pendant__line">
                        {line3 || '\u00a0'}
                    </div>
                ) : null}
            </div>

            <div className={labelClass}>{label}</div>
        </div>
    );
};

export default PendantPreview;
